import { html, nothing } from "lit";
import { filterSubagentRunCards } from "../chat/subagent-run-display.ts";
import { renderSubagentRunsToolbar } from "../chat/subagent-run-row.ts";
import {
  fleetSummaryHeadline,
  resolveSubagentActivityPhase,
  subagentPhaseBadgeClass,
  subagentPhaseLabel,
  summarizeSubagentFleet,
  type SubagentFleetSummary,
} from "../chat/subagent-run-status.ts";
import type { ChatSubagentCard } from "../ui-types.ts";
import { renderFleetSubagentRunCard } from "./fleet-subagent-run-card.ts";

export function filterSubagentCardsForDisplay(
  cards: ChatSubagentCard[],
  filter: "all" | "running",
): ChatSubagentCard[] {
  if (filter === "all") {
    return cards;
  }
  return cards.filter((c) => resolveSubagentActivityPhase(c) === "ongoing");
}

export function partitionSubagentOrchestrations(cards: ChatSubagentCard[]): {
  groups: Array<{ orchestrationId: string; cards: ChatSubagentCard[] }>;
  standalone: ChatSubagentCard[];
} {
  const byOrch = new Map<string, ChatSubagentCard[]>();
  const standalone: ChatSubagentCard[] = [];
  const sorted = [...cards].sort((a, b) => a.createdAt - b.createdAt);
  for (const c of sorted) {
    const oid = c.orchestrationId?.trim();
    if (oid) {
      const list = byOrch.get(oid) ?? [];
      list.push(c);
      byOrch.set(oid, list);
    } else {
      standalone.push(c);
    }
  }
  const groups = [...byOrch.entries()].map(([orchestrationId, groupCards]) => ({
    orchestrationId,
    cards: groupCards,
  }));
  return { groups, standalone };
}

/**
 * Build a parent → children tree from a flat list of subagent cards.
 *
 * A card `X` is treated as the parent of card `Y` when
 * `Y.parentSessionKey === X.childSessionKey` — i.e. Y was spawned by
 * X's child session. Cards without a matching parent in the supplied
 * set are returned as roots (with `parentId === null`).
 *
 * Roots are returned in `createdAt` order, children are sorted by the
 * same key. Stable ids are derived from `childSessionKey` (falling
 * back to `runId` / `id`) so the renderer can use them as DOM keys
 * and so the expand/collapse state in the caller can be keyed on
 * `parentId` even when the parent is no longer in scope (children
 * of absent parents still hang off a synthetic root).
 */
export function buildSubagentHierarchy(cards: ChatSubagentCard[]): {
  roots: SubagentHierarchyNode[];
  byId: Map<string, SubagentHierarchyNode>;
} {
  const byId = new Map<string, SubagentHierarchyNode>();
  const childrenByParent = new Map<string, ChatSubagentCard[]>();

  const keyOf = (c: ChatSubagentCard): string =>
    c.childSessionKey?.trim() || c.runId || c.id;

  // First pass: create a node per card.
  const sorted = [...cards].sort((a, b) => a.createdAt - b.createdAt);
  for (const c of sorted) {
    const key = keyOf(c);
    if (!byId.has(key)) {
      byId.set(key, { card: c, id: key, parentId: null, children: [] });
    }
  }

  // Second pass: link each card to its parent (if any parent card exists in scope).
  for (const c of sorted) {
    const parentKey = c.parentSessionKey?.trim();
    if (!parentKey) {
      continue;
    }
    if (!byId.has(parentKey)) {
      // Parent not in scope; treat as root so the card still renders.
      continue;
    }
    const list = childrenByParent.get(parentKey) ?? [];
    list.push(c);
    childrenByParent.set(parentKey, list);
    const myKey = keyOf(c);
    byId.get(myKey)!.parentId = parentKey;
  }

  // Third pass: build child arrays on the parent nodes.
  for (const [parentKey, childCards] of childrenByParent.entries()) {
    const parent = byId.get(parentKey);
    if (!parent) {
      continue;
    }
    parent.children = childCards
      .map((c) => byId.get(keyOf(c))!)
      .filter(Boolean)
      .sort((a, b) => a.card.createdAt - b.card.createdAt);
  }

  const roots = [...byId.values()]
    .filter((n) => n.parentId === null)
    .sort((a, b) => a.card.createdAt - b.card.createdAt);

  return { roots, byId };
}

export type SubagentHierarchyNode = {
  card: ChatSubagentCard;
  /** Stable id (childSessionKey || runId || id). */
  id: string;
  /** Parent node id, or `null` for roots. */
  parentId: string | null;
  children: SubagentHierarchyNode[];
};

/**
 * A card is a "parent" in the user-visible sense iff it has at least
 * one child card in the supplied set. We treat absent parents (cards
 * whose own parent is not in scope) the same as roots: the renderer
 * only adds the parent/expand affordance when `node.children.length > 0`.
 */
export function isSubagentParent(node: SubagentHierarchyNode): boolean {
  return node.children.length > 0;
}

function orchestrationPhaseSummary(cards: ChatSubagentCard[]): string {
  const summary = summarizeSubagentFleet(cards);
  const parts: string[] = [];
  if (summary.ongoing > 0) {
    parts.push(`${summary.ongoing} in progress`);
  }
  if (summary.completed > 0) {
    parts.push(`${summary.completed} completed`);
  }
  if (summary.failed > 0) {
    parts.push(`${summary.failed} failed`);
  }
  if (summary.idle > 0) {
    parts.push(`${summary.idle} idle`);
  }
  return parts.join(" · ") || "—";
}

function renderActivityLegend(summary: SubagentFleetSummary) {
  const items: Array<{ key: string; label: string; count: number; className: string }> = [
    {
      key: "ongoing",
      label: "In progress",
      count: summary.ongoing,
      className: "active",
    },
    {
      key: "completed",
      label: "Completed",
      count: summary.completed,
      className: "done",
    },
    { key: "failed", label: "Failed", count: summary.failed, className: "error" },
    { key: "defunct", label: "Defunct", count: summary.defunct, className: "defunct" },
    { key: "idle", label: "Idle", count: summary.idle, className: "idle" },
  ];
  return html`
    <div class="fleet-map-legend fleet-subagent-summary__legend" aria-label="Run status legend">
      ${items.map(
        (item) => html`
          <span
            class="fleet-map-legend__item fleet-map-legend__item--${item.className} ${item.count > 0 ? "fleet-map-legend__item--has-count" : ""}"
          >
            ${item.label} (${item.count})
          </span>
        `,
      )}
    </div>
  `;
}

function renderFleetSummaryBanner(summary: SubagentFleetSummary) {
  return html`
    <div
      class="fleet-subagent-summary fleet-subagent-summary--${summary.overall}"
      role="status"
      aria-live="polite"
    >
      <span class="fleet-subagent-summary__headline">${fleetSummaryHeadline(summary)}</span>
      <span class="fleet-subagent-summary__meta muted"
        >${summary.total} run${summary.total === 1 ? "" : "s"} in this chat</span
      >
    </div>
  `;
}

function renderStatusTable(cards: ChatSubagentCard[]) {
  if (cards.length === 0) {
    return nothing;
  }
  const sorted = [...cards].toSorted((a, b) => a.createdAt - b.createdAt);
  return html`
    <table class="fleet-chat-delegation-map__table fleet-subagent-activity-panel__table">
      <thead>
        <tr>
          <th scope="col">Task</th>
          <th scope="col">Agent</th>
          <th scope="col">Status</th>
          <th scope="col">Phase</th>
        </tr>
      </thead>
      <tbody>
        ${sorted.map((card) => {
          const phase = resolveSubagentActivityPhase(card);
          const badge = subagentPhaseBadgeClass(phase);
          return html`
            <tr>
              <td>${card.task?.trim() || card.label}</td>
              <td class="mono muted">@${card.agentId}</td>
              <td>
                <span class="fleet-map-node__badge fleet-map-node__badge--${badge}">
                  ${subagentPhaseLabel(phase)}
                </span>
              </td>
              <td class="muted">${card.phase ?? (card.toolHint ? "working" : "—")}</td>
            </tr>
          `;
        })}
      </tbody>
    </table>
  `;
}

export type FleetSubagentActivityPanelProps = {
  sessionKey: string;
  subagents: ChatSubagentCard[];
  filter: "all" | "running";
  subagentExpanded: Record<string, boolean>;
  /**
   * `key` is the card id; `value` true = expanded. The tree group
   * (parent) uses a synthetic key `tree:<parentId>` so the parent's
   * expand/collapse state is independent of the per-card expand state.
   */
  onToggleSubagentExpand: (id: string) => void;
  onFilterChange: (next: "all" | "running") => void;
  onCollapseAll: () => void;
  onJumpLatestRunning?: () => void;
  hasRunning: boolean;
  onOpenSubagentSession?: (params: { childKey: string; parentKey: string; label: string }) => void;
  /**
   * Optional fleet-wide aggregation: stashed subagent cards grouped by their
   * parent session. When present, the panel renders a header for each parent
   * session and shows those cards in addition to the active session's cards.
   * This makes the Subagents tab a true fleet-wide view rather than being
   * scoped to the currently-active chat tab.
   */
  otherSessionBuckets?: Array<{
    parentKey: string;
    cards: import("../ui-types.ts").ChatSubagentCard[];
  }>;
  /**
   * Currently selected parent card id (its `childSessionKey || runId || id`).
   * When set, every child of that parent is visually highlighted in the
   * tree, and the parent's own row receives a `aria-current="true"`.
   * Selection is purely a UI affordance — pass `onSelectParent` to make
   * parent rows clickable. Clear by passing the same id again (toggle).
   */
  selectedParentId?: string | null;
  onSelectParent?: (parentId: string | null) => void;
};

function renderCardRow(
  card: ChatSubagentCard,
  props: FleetSubagentActivityPanelProps,
  ctx: {
    node: SubagentHierarchyNode;
    depth: number;
    isLast: boolean;
    parentIdChain: string[];
  },
): ReturnType<typeof html> {
  const expanded = Boolean(props.subagentExpanded[card.id]);
  const parent = ctx.parentIdChain.length > 0 ? ctx.parentIdChain[ctx.parentIdChain.length - 1] : null;
  const isHighlighted = Boolean(
    props.selectedParentId &&
      parent &&
      props.selectedParentId === parent,
  );
  // A card is a tree-parent iff it has at least one child in scope.
  // We surface the "click to select this parent" affordance only for
  // those rows, so clicking a leaf doesn't toggle an empty selection.
  const isParentInTree = ctx.node.children.length > 0;
  const isParentSelected = isParentInTree && props.selectedParentId === ctx.node.id;
  return html`
    <div
      class="fleet-subagent-activity-panel__row ${isHighlighted ? "fleet-subagent-activity-panel__row--highlighted" : ""}"
      role="listitem"
    >
      ${renderFleetSubagentRunCard({
        card,
        expanded,
        depth: ctx.depth,
        isLastChild: ctx.isLast,
        parentId: parent,
        parentLabel: parent ? labelForParentId(parent, props) : null,
        isHighlightedChild: isHighlighted,
        onSelectParent:
          isParentInTree && props.onSelectParent
            ? () => props.onSelectParent?.(isParentSelected ? null : ctx.node.id)
            : null,
        isParentSelected,
        onToggle: () => props.onToggleSubagentExpand(card.id),
        onOpenSessionTab:
          card.childSessionKey && props.onOpenSubagentSession
            ? () =>
                props.onOpenSubagentSession?.({
                  childKey: card.childSessionKey,
                  parentKey: props.sessionKey,
                  label: card.label,
                })
            : undefined,
      })}
      ${ctx.node.children.length > 0
        ? renderTreeGroup(ctx.node, props, ctx.parentIdChain, ctx.depth + 1)
        : nothing}
    </div>
  `;
}

function labelForParentId(
  parentId: string,
  props: FleetSubagentActivityPanelProps,
): string {
  const allCards = collectAllCards(props);
  const card = allCards.get(parentId);
  if (!card) {
    return parentId.split(":").slice(-2).join(":") || parentId;
  }
  const task = card.task?.trim();
  if (task) {
    return task.length > 48 ? `${task.slice(0, 47)}…` : task;
  }
  const label = card.label?.trim();
  if (label) {
    return label;
  }
  return card.agentId ? `@${card.agentId}` : parentId;
}

function collectAllCards(
  props: FleetSubagentActivityPanelProps,
): Map<string, ChatSubagentCard> {
  const map = new Map<string, ChatSubagentCard>();
  for (const c of props.subagents) {
    const key = c.childSessionKey?.trim() || c.runId || c.id;
    map.set(key, c);
  }
  for (const bucket of props.otherSessionBuckets ?? []) {
    for (const c of bucket.cards) {
      const key = c.childSessionKey?.trim() || c.runId || c.id;
      if (!map.has(key)) {
        map.set(key, c);
      }
    }
  }
  return map;
}

function renderTreeGroup(
  node: SubagentHierarchyNode,
  props: FleetSubagentActivityPanelProps,
  parentChain: string[],
  childDepth: number,
): ReturnType<typeof html> {
  const groupKey = `tree:${node.id}`;
  const expanded = Boolean(props.subagentExpanded[groupKey]);
  const childCount = node.children.length;
  const nextChain = [...parentChain, node.id];

  return html`
    <div
      class="fleet-subagent-activity-panel__tree-group"
      data-tree-depth=${childDepth}
      role="group"
      aria-label=${`Children of ${labelForParentId(node.id, props)}`}
    >
      <button
        type="button"
        class="fleet-subagent-activity-panel__tree-toggle"
        aria-expanded=${expanded}
        @click=${() => props.onToggleSubagentExpand(groupKey)}
      >
        <span class="fleet-subagent-activity-panel__tree-chevron" aria-hidden="true">
          ${expanded ? "▼" : "▶"}
        </span>
        <span class="fleet-subagent-activity-panel__tree-toggle-label">
          ${expanded ? "Hide" : "Show"} ${childCount} child
          subagent${childCount === 1 ? "" : "s"}
        </span>
      </button>
      ${expanded
        ? html`
            <ol
              class="fleet-subagent-activity-panel__tree-list"
              role="list"
            >
              ${node.children.map((child, idx): ReturnType<typeof html> => {
                const isLast = idx === node.children.length - 1;
                return renderCardRow(child.card, props, {
                  node: child,
                  depth: childDepth,
                  isLast,
                  parentIdChain: nextChain,
                });
              })}
            </ol>
          `
        : nothing}
    </div>
  `;
}

export function renderFleetSubagentActivityPanel(props: FleetSubagentActivityPanelProps) {
  const subagentRuns = filterSubagentRunCards(props.subagents);
  const summary = summarizeSubagentFleet(subagentRuns);
  const filtered = filterSubagentCardsForDisplay(subagentRuns, props.filter);
  const { groups } = partitionSubagentOrchestrations(filtered);

  // Build the parent → children tree from the filtered set. Cards whose
  // `parentSessionKey` matches another card's `childSessionKey` are
  // nested under that parent; the rest render as roots. The orchestration
  // grouping still wraps the root of each orchestrated fan-out.
  const { roots, byId } = buildSubagentHierarchy(filtered);
  // Track orchestration roots so the renderer can wrap them in the
  // existing orchestration section chrome (header + task line + badge).
  // A card is an "orchestration root" if it (a) has a non-empty
  // orchestrationId and (b) has no parent in scope — i.e. it's a top-
  // level node whose orchestration group is rooted at it.
  const orchestrationRootById = new Map<string, { orchestrationId: string; cards: ChatSubagentCard[] }>();
  for (const g of groups) {
    for (const c of g.cards) {
      const key = c.childSessionKey?.trim() || c.runId || c.id;
      const node = byId.get(key);
      if (node && node.parentId === null) {
        orchestrationRootById.set(key, { orchestrationId: g.orchestrationId, cards: g.cards });
      }
    }
  }

  const hasAny = roots.length > 0 || subagentRuns.length > 0;

  return html`
    <div class="fleet-subagent-activity-panel">
      <p class="muted fleet-subagent-activity-panel__intro">
        <strong>Subagent activity</strong> — whether work is still running, complete, failed, or
        idle for this chat. Same status styling as <strong>Fleet map</strong>.
      </p>

      ${subagentRuns.length > 0 ? renderFleetSummaryBanner(summary) : nothing}
      ${subagentRuns.length > 0 ? renderActivityLegend(summary) : nothing}
      ${
        subagentRuns.length > 0
          ? renderSubagentRunsToolbar({
              filter: props.filter,
              onFilterChange: props.onFilterChange,
              onCollapseAll: props.onCollapseAll,
              onJumpLatestRunning: props.onJumpLatestRunning,
              hasRunning: props.hasRunning,
            })
          : nothing
      }
      ${
        !hasAny || filtered.length === 0
          ? html`
              <p class="muted fleet-subagent-activity-panel__empty">
                ${
                  subagentRuns.length === 0
                    ? "No subagents for this session yet. Mention an agent (@code), use @all, or check Orchestrate with @all before sending a multi-agent prompt."
                    : "No runs match the Active filter. Switch to All to see completed and idle runs."
                }
              </p>
            `
          : html`
              <div class="fleet-subagent-activity-panel__list" role="list">
                ${roots.map((root, idx) => {
                  const isLastRoot = idx === roots.length - 1;
                  const orchInfo = orchestrationRootById.get(root.id);
                  if (orchInfo) {
                    const task = orchInfo.cards[0]?.task?.trim() || "Orchestrated fan-out";
                    const groupSummary = summarizeSubagentFleet(orchInfo.cards);
                    return html`
                      <section
                        class="fleet-map-node fleet-map-node--orchestration fleet-subagent-activity-panel__orchestration"
                        role="listitem"
                        aria-label=${`Orchestration: ${orchInfo.cards.length} subagents`}
                      >
                        <header class="fleet-subagent-activity-panel__orchestration-head">
                          <span class="fleet-map-node__title"
                            >Orchestration · ${orchInfo.cards.length} agents</span
                          >
                          <span
                            class="fleet-map-node__badge fleet-map-node__badge--${groupSummary.overall === "in_progress" ? "running" : groupSummary.overall === "needs_attention" ? "error" : "done"}"
                          >
                            ${orchestrationPhaseSummary(orchInfo.cards)}
                          </span>
                        </header>
                        <p class="fleet-map-node__task">${task}</p>
                        <div class="fleet-subagent-activity-panel__orchestration-rows">
                          ${renderCardRow(root.card, props, {
                            node: root,
                            depth: 0,
                            isLast: isLastRoot,
                            parentIdChain: [],
                          })}
                        </div>
                      </section>
                    `;
                  }
                  return renderCardRow(root.card, props, {
                    node: root,
                    depth: 0,
                    isLast: isLastRoot,
                    parentIdChain: [],
                  });
                })}
              </div>
              ${renderStatusTable(subagentRuns)}
            `
      }
      ${
        props.otherSessionBuckets && props.otherSessionBuckets.length > 0
          ? renderOtherSessionBuckets(props)
          : nothing
      }
    </div>
  `;
}

function renderOtherSessionBuckets(props: FleetSubagentActivityPanelProps) {
  const buckets = (props.otherSessionBuckets ?? []).filter((b) => b.cards.length > 0);
  if (buckets.length === 0) {
    return nothing;
  }
  return html`
    <section class="fleet-subagent-activity-panel__buckets" aria-label="Other chat sessions">
      <h4 class="fleet-subagent-activity-panel__buckets-head">
        Subagents in other open chat sessions
      </h4>
      ${buckets.map((bucket) => {
        const { roots } = buildSubagentHierarchy(bucket.cards);
        return html`
          <details
            class="fleet-subagent-activity-panel__bucket"
            open=${bucket.cards.some((c) => resolveSubagentActivityPhase(c) === "ongoing")}
          >
            <summary>
              <span class="fleet-subagent-activity-panel__bucket-parent">${bucket.parentKey}</span>
              <span class="muted"> · ${bucket.cards.length} card(s)</span>
            </summary>
            <div class="fleet-subagent-activity-panel__bucket-list" role="list">
              ${roots.map((root, idx) => {
                const isLast = idx === roots.length - 1;
                return renderCardRow(root.card, props, {
                  node: root,
                  depth: 0,
                  isLast,
                  parentIdChain: [],
                });
              })}
            </div>
          </details>
        `;
      })}
    </section>
  `;
}
